import { useMemo } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { cx } from '@utils/cx'
import Tile from './Tile'
import styles from './PuzzleBoard.module.css'

/**
 * PuzzleBoard - Pure component for rendering puzzle tiles
 */

export default function PuzzleBoard ({
  board = [],
  size,
  imageSrc,
  interactive = true,
  inputBlocked = false,
  canMoveTile = () => false,
  onTileClick = () => {},
  className = '',
  ...props
}) {
  const { t } = useLanguage()

  const emptyValue = useMemo(() => size * size - 1, [size])

  // Don't render if no board data
  if (!board.length || !size) {
    return null
  }

  return (
    <div
      className={cx(styles.puzzleBoard, className)}
      role='grid'
      aria-label={t.puzzle.access.board}
      style={{ '--puzzle-size': size }}
      {...props}
    >
      {board.map((tileValue, index) => {
        const isEmpty = tileValue === emptyValue
        const movable = interactive && !isEmpty && canMoveTile(index) && !inputBlocked

        // Accessibility: Row and column for screen readers
        const curRow = Math.floor(index / size) + 1
        const curCol = (index % size) + 1

        const srLabel = isEmpty
          ? t.puzzle.access.empty
          : `${t.puzzle.access.tiles.tile} ${tileValue + 1}, ` +
            `${t.puzzle.access.tiles.row} ${curRow}, ` +
            `${t.puzzle.access.tiles.col} ${curCol}` +
            (movable ? `, ${t.puzzle.access.tiles.movable}` : '')

        return (
          <Tile
            key={index}
            tileValue={tileValue}
            index={index}
            isEmpty={isEmpty}
            movable={movable}
            imageSrc={imageSrc}
            size={size}
            srLabel={srLabel}
            onClick={interactive ? onTileClick : undefined}
          />
        )
      })}
    </div>
  )
}
